import React, { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
  {
    title: 'Conversational AI Builder',
    tagline: 'Chat naturally with AI to draft, reformat, and refine your resume bullet points in seconds.',
    renderIllustration: () => (
      <div className="h-48 rounded-2xl bg-brand-purple/[0.03] dark:bg-brand-purple/[0.06] border border-brand-purple/10 dark:border-brand-purple/20 flex items-center justify-center relative overflow-hidden group">
        {/* Floating background glowing orb */}
        <div className="absolute w-24 h-24 bg-brand-gz-amber/10 rounded-full blur-2xl top-4 left-6 group-hover:scale-125 transition-transform duration-700" />
        
        {/* Mini Resume Card */}
        <div className="w-[90px] h-[115px] bg-white border border-zinc-200 shadow-lg rounded-xl absolute left-8 top-5 -rotate-6 z-10 p-2.5 flex flex-col gap-1 transition-transform duration-500 group-hover:rotate-0">
          <div className="text-[8px] font-extrabold text-[#14213D] leading-tight">Alex Miller</div>
          <div className="text-[5px] text-zinc-500 font-bold leading-none -mt-0.5">Software Engineer</div>
          
          <div className="border-t border-zinc-100 my-1"></div>
          
          <div className="text-[5.5px] font-black text-[#14213D] tracking-wider">PROJECTS</div>
          <div className="text-[5.5px] font-bold text-zinc-805 leading-tight bg-[#14213D]/5 p-1 rounded border border-[#14213D]/10">
            ✦ E-Commerce App
          </div>
          <div className="text-[4px] text-zinc-500 leading-snug">Built online store platform.</div>
        </div>

        {/* Chat Window Mockup */}
        <div className="w-[170px] bg-white border border-zinc-200/80 shadow-2xl rounded-2xl p-3 absolute right-6 bottom-4 z-20 flex flex-col gap-2 transition-transform duration-500 group-hover:translate-y-[-4px]">
          {/* User Message */}
          <div className="bg-[#14213D]/10 text-[8.5px] font-semibold text-[#14213D] px-2.5 py-1.5 rounded-2xl rounded-tr-none self-end max-w-[130px] border border-[#14213D]/20">
            Add Web Dev project
          </div>
          {/* AI Response */}
          <div className="bg-zinc-50 text-[8.5px] font-medium text-zinc-700 px-2.5 py-1.5 rounded-2xl rounded-tl-none self-start max-w-[145px] border border-zinc-150 flex gap-1.5 items-start">
            <span className="text-[#FCA311] font-bold">✦</span>
            <span>Added E-Commerce App!</span>
          </div>
        </div>
      </div>
    )
  },
  {
    title: 'Real-time Preview Engine',
    tagline: 'Watch your changes compile and render instantly side-by-side as you type or edit with AI.',
    renderIllustration: () => (
      <div className="h-48 rounded-2xl bg-brand-purple/[0.03] dark:bg-brand-purple/[0.06] border border-brand-purple/10 dark:border-brand-purple/20 flex items-center justify-center relative overflow-hidden group">
        <div className="absolute w-24 h-24 bg-brand-gz-amber/10 rounded-full blur-2xl bottom-4 right-6 group-hover:scale-125 transition-transform duration-700" />
        
        {/* Editor Layout on Left */}
        <div className="w-[115px] h-[115px] bg-white border border-zinc-200 shadow-xl rounded-xl p-2.5 absolute left-6 translate-y-2 flex flex-col gap-1 transition-transform duration-500 group-hover:-translate-x-1">
          <div className="flex gap-1 items-center mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
            <span className="text-[5px] text-zinc-400 font-bold ml-1">Editor</span>
          </div>
          <div className="text-[6.5px] font-bold text-[#14213D]">Experience</div>
          <div className="text-[5px] text-zinc-500 leading-normal border border-zinc-100 rounded p-1 bg-zinc-50">
            • Built SaaS platform using React & Node.
          </div>
        </div>

        {/* Sync Animation Ring in Middle */}
        <div className="absolute z-20 w-8 h-8 rounded-full bg-[#14213D]/20 border border-[#14213D]/40 flex items-center justify-center animate-pulse shadow-[0_0_15px_rgba(20,33,61,0.3)]">
          <svg className="w-4 h-4 text-[#14213D] animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 4.89M9 11l3 3L22 4" />
          </svg>
        </div>

        {/* Live Resume Layout on Right */}
        <div className="w-[110px] h-[130px] bg-white border border-zinc-200 shadow-2xl rounded-xl p-3 absolute right-6 -translate-y-1 flex flex-col gap-1 transition-transform duration-500 group-hover:scale-105 group-hover:translate-x-1">
          <div className="flex items-center gap-1.5 pb-1 border-b border-zinc-100">
            <div className="w-3.5 h-3.5 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
              <span className="text-[7px] font-bold">✓</span>
            </div>
            <div className="text-[7px] font-extrabold text-zinc-805">Alex Miller</div>
          </div>
          <div className="text-[5.5px] font-bold text-zinc-800 mt-1">EXPERIENCE</div>
          <div className="text-[5px] text-zinc-650 font-semibold leading-normal bg-emerald-50/50 p-1 rounded border border-emerald-100">
            • Built SaaS platform using React & Node.
          </div>
          <div className="text-[4px] text-zinc-400">Deployed to AWS with CI/CD.</div>
        </div>
      </div>
    )
  },
  {
    title: 'Smart ATS Scorecard',
    tagline: 'Evaluate keyword density, check structural completeness, and clear recruiter screenings.',
    renderIllustration: () => (
      <div className="h-48 rounded-2xl bg-brand-purple/[0.03] dark:bg-brand-purple/[0.06] border border-brand-purple/10 dark:border-brand-purple/20 flex items-center justify-center relative overflow-hidden group">
        <div className="absolute w-24 h-24 bg-brand-gz-amber/10 rounded-full blur-2xl top-2 right-4 group-hover:scale-125 transition-transform duration-700" />
        
        {/* Credit Meter Card */}
        <div className="w-[125px] bg-white border border-zinc-200 p-2.5 rounded-xl shadow-lg absolute left-6 -translate-y-4 z-10 flex flex-col gap-1 transition-transform duration-500 group-hover:scale-105">
          <div className="flex items-center gap-1">
            <div className="w-4.5 h-4.5 rounded-full bg-[#FCA311]/20 flex items-center justify-center text-[#FCA311] font-extrabold text-[9px] shrink-0">
              $
            </div>
            <span className="text-[8.5px] font-bold text-zinc-700">Credit Balance</span>
          </div>
          <div className="flex items-baseline gap-1 mt-0.5">
            <span className="text-base font-black text-[#FCA311]">24</span>
            <span className="text-[7px] text-zinc-450 font-bold">Credits</span>
          </div>
        </div>

        {/* ATS Circle Gauge Card */}
        <div className="w-[135px] bg-white border border-zinc-200 shadow-2xl rounded-2xl p-3 absolute right-6 translate-y-3 z-20 flex flex-col items-center gap-1.5 transition-transform duration-500 group-hover:translate-y-1">
          <div className="flex items-center justify-between w-full">
            <span className="text-[9px] font-bold text-zinc-800">ATS Rating</span>
            <span className="text-[9px] font-black text-emerald-500">+12 pts</span>
          </div>
          
          {/* Progress Ring Visual */}
          <div className="relative w-13 h-13 flex items-center justify-center">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <path className="text-zinc-100" strokeWidth="3.5" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              <path className="text-emerald-500" strokeDasharray="88, 100" strokeWidth="3.5" strokeLinecap="round" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            </svg>
            <span className="absolute text-[10.5px] font-black text-zinc-900">88%</span>
          </div>
          
          <div className="text-[6px] font-bold text-emerald-650 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
            ✓ Ready for Job Applications
          </div>
        </div>
      </div>
    )
  },
  {
    title: 'Interactive Design System',
    tagline: 'Swap typographic scales, template fonts, and brand color palettes instantly.',
    renderIllustration: () => (
      <div className="h-48 rounded-2xl bg-brand-purple/[0.03] dark:bg-brand-purple/[0.06] border border-brand-purple/10 dark:border-brand-purple/20 flex items-center justify-center relative overflow-hidden group">
        <div className="absolute w-24 h-24 bg-brand-gz-amber/10 rounded-full blur-2xl bottom-2 left-4 group-hover:scale-125 transition-transform duration-700" />
        
        {/* Style Selection Panel */}
        <div className="w-[125px] bg-white border border-zinc-200 rounded-xl shadow-lg p-2.5 absolute left-6 -translate-y-2 z-10 flex flex-col gap-1.5 transition-transform duration-500 group-hover:scale-105">
          <span className="text-[8.5px] font-bold text-zinc-700">Theme Palette</span>
          <div className="flex gap-1.5 justify-between">
            <div className="w-4.5 h-4.5 rounded-full bg-[#14213D] border border-white cursor-pointer ring-2 ring-[#14213D]/30"></div>
            <div className="w-4.5 h-4.5 rounded-full bg-[#10B981] cursor-pointer"></div>
            <div className="w-4.5 h-4.5 rounded-full bg-[#FCA311] cursor-pointer"></div>
            <div className="w-4.5 h-4.5 rounded-full bg-[#EC4899] cursor-pointer"></div>
          </div>
          <span className="text-[5px] text-zinc-400 font-semibold mt-0.5">Template: Space Grotesk</span>
        </div>

        {/* Theme Styled Resume */}
        <div className="w-[110px] h-[135px] bg-white border border-zinc-200 shadow-2xl rounded-xl p-3 absolute right-8 translate-y-3 z-20 flex flex-col gap-1.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1">
          {/* Top colored strip indicating template style */}
          <div className="h-4 w-full bg-[#14213D] rounded-md flex items-center justify-between px-1.5">
            <div className="text-[5px] text-white font-extrabold leading-none">Alex Miller</div>
            <div className="w-1.5 h-1.5 rounded-full bg-white/40"></div>
          </div>
          <div className="text-[5.5px] font-bold text-zinc-855 mt-0.5">EDUCATION</div>
          <div className="flex flex-col gap-1">
            <div className="text-[5px] font-bold text-zinc-700 leading-none">MS in Computer Science</div>
            <div className="text-[4px] text-zinc-400">Stanford University · GPA 3.8</div>
          </div>
        </div>
      </div>
    )
  },
  {
    title: 'Tailored Cover Letters',
    tagline: 'Generate matching cover letters formatted to sync with your resume style and target job.',
    renderIllustration: () => (
      <div className="h-48 rounded-2xl bg-brand-purple/[0.03] dark:bg-brand-purple/[0.06] border border-brand-purple/10 dark:border-brand-purple/20 flex items-center justify-center relative overflow-hidden group">
        <div className="absolute w-24 h-24 bg-brand-gz-amber/10 rounded-full blur-2xl top-2 left-6 group-hover:scale-125 transition-transform duration-700" />
        
        {/* Cover Letter Document */}
        <div className="w-[130px] h-[150px] bg-white rounded-xl shadow-2xl border border-zinc-200 p-3.5 flex flex-col gap-1.5 -rotate-3 transition-transform duration-500 group-hover:rotate-0 group-hover:scale-105 z-10">
          <div className="flex gap-2 items-center pb-2 border-b border-zinc-100">
            <div className="w-5 h-5 rounded bg-[#14213D]/10 text-[#14213D] text-[8px] font-bold flex items-center justify-center shrink-0">
              DJ
            </div>
            <div className="text-[7.5px] font-extrabold text-zinc-855">Cover Letter</div>
          </div>
          <div className="flex flex-col gap-1 mt-1 text-[5px] text-zinc-650 leading-relaxed font-semibold">
            <div>Dear Hiring Manager,</div>
            <div>I am writing to express my strong interest in the Software Engineer position...</div>
            <div className="text-[4.5px] text-zinc-450 mt-1 font-bold">Sincerely,</div>
            <div className="text-[5px] text-zinc-800 font-extrabold -mt-0.5">Alex Miller</div>
          </div>
        </div>
        
        {/* Magic Wand overlay with sparks */}
        <div className="absolute right-8 top-6 z-20 text-[#14213D] animate-float">
          <svg className="w-12 h-12 text-[#14213D] filter drop-shadow-[0_0_12px_rgba(20,33,61,0.6)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 4V2M15 16v-2M8 9h2M20 9h-2M17.8 5.2l-1.4 1.4M7.6 15.4l-1.4 1.4M6.2 5.2l1.4 1.4M16.4 15.4l1.4 1.4" />
            <path d="M19 11l-8 8-4-4 8-8 4 4z" fill="currentColor" fillOpacity="0.2" />
          </svg>
          <span className="absolute -top-1 -right-1 text-xs text-[#FCA311] animate-pulse">✦</span>
          <span className="absolute top-5 -left-1 text-[9px] text-[#B9C2D0]">✦</span>
        </div>
      </div>
    )
  },
  {
    title: 'Job-Targeted Variants',
    tagline: 'Analyze target job descriptions and automatically build high-match variants in seconds.',
    renderIllustration: () => (
      <div className="h-48 rounded-2xl bg-brand-purple/[0.03] dark:bg-brand-purple/[0.06] border border-brand-purple/10 dark:border-brand-purple/20 flex items-center justify-center relative overflow-hidden group">
        <div className="absolute w-24 h-24 bg-brand-gz-amber/10 rounded-full blur-2xl bottom-4 right-6 group-hover:scale-125 transition-transform duration-700" />
        
        {/* Job Description (JD) Card */}
        <div className="w-[125px] h-[105px] bg-white border border-zinc-200 shadow-xl rounded-xl p-2.5 absolute left-6 -translate-y-4 z-10 flex flex-col gap-1 transition-transform duration-500 group-hover:scale-105">
          <span className="text-[7.5px] font-bold text-zinc-550">Target Job Description</span>
          <div className="text-[5px] text-zinc-500 leading-normal border border-zinc-100 rounded p-1 bg-zinc-50 font-semibold">
            Looking for a developer skilled in <span className="bg-[#14213D]/10 text-[#14213D] px-0.5 rounded">React</span>, <span className="bg-[#14213D]/10 text-[#14213D] px-0.5 rounded">Node</span>, and <span className="bg-[#14213D]/10 text-[#14213D] px-0.5 rounded">AWS</span>...
          </div>
        </div>

        {/* Sync arrow indicator */}
        <div className="absolute z-20 w-8 h-8 rounded-full bg-[#14213D]/20 border border-[#14213D]/40 flex items-center justify-center animate-pulse shadow-[0_0_15px_rgba(20,33,61,0.3)]">
          <svg className="w-4 h-4 text-[#14213D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>

        {/* Tailored Resume on Right */}
        <div className="w-[110px] h-[130px] bg-white border border-zinc-200 shadow-2xl rounded-xl p-3 absolute right-6 translate-y-3 z-20 flex flex-col gap-1.5 transition-transform duration-500 group-hover:translate-x-1">
          <div className="flex items-center gap-1.5 pb-1 border-b border-zinc-100">
            <div className="w-3.5 h-3.5 rounded-full bg-[#14213D]/25 text-[#14213D] flex items-center justify-center shrink-0 text-[6px] font-black">✦</div>
            <div className="text-[7px] font-extrabold text-zinc-855">Tailored Resume</div>
          </div>
          <div className="text-[5.5px] font-bold text-zinc-800">SKILLS</div>
          <div className="flex flex-wrap gap-1">
            <span className="text-[4.5px] bg-[#14213D] text-white px-1 py-0.5 rounded font-bold">React</span>
            <span className="text-[4.5px] bg-[#14213D] text-white px-1 py-0.5 rounded font-bold">Node</span>
            <span className="text-[4.5px] bg-[#14213D] text-white px-1 py-0.5 rounded font-bold">AWS</span>
            <span className="text-[4.5px] bg-zinc-100 text-zinc-650 px-1 py-0.5 rounded font-semibold">Git</span>
          </div>
          <div className="text-[4.5px] text-emerald-500 font-bold mt-1">✓ 98% ATS Keyword Match</div>
        </div>
      </div>
    )
  }
];

export default function FeaturesSection() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="features"
      className="py-10 sm:py-24 transition-colors duration-300 overflow-hidden"
      style={{ background: 'var(--lp-bg)' }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <p className="text-xs font-bold text-brand-gz-purple tracking-[0.2em] uppercase mb-4">
            FEATURES
          </p>
          <h2
            className="font-display-gz font-black leading-tight tracking-tight mx-auto max-w-4xl"
            style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'var(--lp-text)' }}
          >
            6 features to boost your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FCA311] to-[#E59200]">job search</span>
          </h2>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {FEATURES.map((feature, idx) => (
            <div
              key={feature.title}
              className={`flex flex-col gap-4 transform transition-all duration-700 hover:-translate-y-1.5 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${idx * 0.08}s` }}
            >
              {/* Illustration Container */}
              {feature.renderIllustration()}
              
              {/* Title & Tagline */}
              <div className="flex flex-col gap-1">
                <h3
                  className="text-lg md:text-xl font-display-gz font-black tracking-tight"
                  style={{ color: 'var(--lp-text)' }}
                >
                  {feature.title}
                </h3>
                <p 
                  className="text-xs font-medium"
                  style={{ color: 'var(--lp-text-muted)' }}
                >
                  {feature.tagline}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
